import { useEffect, useMemo, useRef } from "react";

import { GuidedTour } from "./GuidedTour";
import { SIDEBAR_TOUR_ID, SIDEBAR_TOUR_STEPS } from "./sidebarTour";
import { localStorageTourPersistence } from "./tourPersistence";
import { useGuidedTour } from "./useGuidedTour";

/**
 * Mounting point for the sidebar walkthrough tour. Lives at the
 * `/home` shell level so the auto-trigger fires once per device on
 * the customer's first authenticated visit.
 *
 * Persistence is wired with `localStorageTourPersistence()` today.
 * When the backend ships `/onboarding/tours/{id}/completed`, swap
 * in `backendTourPersistence(env)` here — the hook + UI don't move.
 * See `foundations/PENDING-WORK.md` → "Tour completion → backend".
 *
 * Account view exposes a "Take tour again" button (see
 * `features/account/AccountView.tsx`) that calls `persistence.reset`
 * + navigates to /home?tour=restart so a fresh mount picks it up
 * via `<SidebarTourMount autostart />`. Keeping the re-launch
 * trigger in the URL means tour state lives in one place
 * (localStorage) — no cross-feature shared context required.
 */

// Trivial helper — extracted so the autostart effect is testable in
// isolation and the useEffect's exhaustive-deps check stays clean
// (start callback is stable from useCallback inside the hook).
const useStartOnce = (autostart: boolean, start: () => void) => {
  const fired = useRef(false);
  useEffect(() => {
    if (!autostart) return undefined;
    if (fired.current) return undefined;
    fired.current = true;
    // Match the auto-trigger delay so DOM has settled before we
    // open the spotlight.
    const t = window.setTimeout(start, 600);
    return () => window.clearTimeout(t);
  }, [autostart, start]);
};

export const SidebarTourMount = ({
  autostart = false,
}: {
  /**
   * When true, ignore the persistence flag and start the tour
   * immediately. Used by the "Take tour again" entry-point so the
   * customer's click reliably re-fires the walkthrough.
   */
  autostart?: boolean;
}) => {
  const persistence = useMemo(() => localStorageTourPersistence(), []);

  const { active, currentStep, close, next, prev, start } = useGuidedTour({
    tourId: SIDEBAR_TOUR_ID,
    totalSteps: SIDEBAR_TOUR_STEPS.length,
    persistence,
    disableAutoTrigger: autostart,
  });

  useStartOnce(autostart, start);

  return (
    <GuidedTour
      active={active}
      steps={SIDEBAR_TOUR_STEPS}
      currentStep={currentStep}
      onClose={close}
      onNext={next}
      onPrev={prev}
    />
  );
};
